import type React from 'react';

/**
 * Shared portal positioning for the unified dropdown system. All dropdowns
 * render in a portal (to escape `overflow: hidden` ancestors) and anchor to
 * their trigger via getBoundingClientRect().
 *
 * - `align: 'stretch'` (default) inherits the trigger's left + width (Type B/C).
 * - `align: 'right'` right-aligns to the trigger instead (Type A action menus).
 * - `gap` is the px offset between trigger and dropdown (0 for the S2 flush
 *   look where the dropdown merges with the trigger border).
 */
interface PositionOptions {
  preferAbove?: boolean;
  align?: 'stretch' | 'right';
  gap?: number;
}

export const getDropdownPosition = (
  triggerEl: HTMLElement,
  { preferAbove = false, align = 'stretch', gap = 4 }: PositionOptions = {},
): React.CSSProperties => {
  const rect = triggerEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const openAbove = preferAbove || spaceBelow < 240;

  const vertical = openAbove
    ? { bottom: window.innerHeight - rect.top + gap }
    : { top: rect.bottom + gap };

  const horizontal =
    align === 'right'
      ? { right: window.innerWidth - rect.right }
      : { left: rect.left, width: rect.width };

  return { position: 'fixed', ...vertical, ...horizontal };
};
